/**
 * 前端类型定义
 * 与后端共享结构（camelCase）
 */

// ===== 基础数据模型 =====

export interface Stock {
  code: string;
  symbol: string;
  name: string;
  market: 'sh' | 'sz';
  board: 'main' | 'star' | 'gem';
  isSt: boolean;
  listingDate: string;
  updatedAt: string;
}

export interface RealtimeQuote {
  code: string;
  name: string;
  price: number;
  changePct: number;
  changeAmount: number;
  open: number;
  prevClose: number;
  high: number;
  low: number;
  volume: number;
  amount: number;
  amplitude: number;
  turnoverRate: number;
  isSt: boolean;
  isLimitUp: boolean;
  isLimitDown: boolean;
}

export interface MarketBreadth {
  upCount: number;
  downCount: number;
  flatCount: number;
  shIndex: number;
  shChangePct: number;
  szIndex: number;
  szChangePct: number;
  cybIndex: number;
  cybChangePct: number;
  pauseBuy: boolean;
  pauseSell: boolean;
  snapshotTime: string;
}

export interface PaginatedQuotes {
  data: RealtimeQuote[];
  total: number;
  page: number;
  size: number;
}

// ===== 条件系统 =====

export type ConditionCategory = 'buy' | 'sell' | 'global';

export type RuleType =
  | 'turnover_rate_consecutive'
  | 'price_from_low'
  | 'price_amplitude'
  | 'exclude_st'
  | 'limit_up_history'
  | 'listing_days'
  | 'profit_threshold'
  | 'loss_threshold'
  | 'market_breadth_pause'
  | 'market_index_pause'
  | 'held_filter'
  | 'sell_cooldown'
  | 'loss_double'
  | 'consecutive_loss_double'
  | 'max_price'
  | 'same_day_sell_block'
  | 'avg_down_buy'
  | 'avg_down_sell'
  /** 量比连续N日≥阈值（2026-09-04 新增） */
  | 'volume_ratio_consecutive'
  /** 每日最多买入只数（2026-09-06 新增） */
  | 'daily_max_buys'
  /** 撞顶时优先选（2026-09-06 新增） */
  | 'buy_sort_by'
  /** 收盘价 ≤ 近 N 日最低点 × (1+pct%)（2026-09-11 新增，0=关闭） */
  | 'screen_low_30d_max_pct'
  /** 近 N 日最低价止损（2026-09-11 新增，0=关闭） */
  | 'loss_30d_low_stop_days'
  /** 最近 N 个交易日至少 M 天上涨（2026-09-16 新增，M<=0=关闭） */
  | 'screen_up_days'
  /** 连续 N 个交易日换手率 < 阈值 → 次日 10:00 卖出（2026-09-22 新增） */
  | 'low_turnover_sell'
  /** 流通市值上限（2026-09-07 新增） */
  | 'float_market_cap_max'
  /** 开盘涨幅过大取消买入（2026-09-07 新增） */
  | 'open_jump_cancel'
  /** 单笔持仓金额上限（2026-09-07 新增） */
  | 'max_portfolio_value';

export interface ConditionRule {
  id?: number;
  category: ConditionCategory;
  ruleType: RuleType;
  operator?: string;
  value?: string;
  params?: Record<string, any>;
  description: string;
  enabled: boolean;
  isPreset: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ParsedCondition {
  ruleType: RuleType;
  operator: string;
  value: string;
  params: Record<string, any>;
  description: string;
  confidence: number;
}

// ===== 交易系统 =====

export interface Holding {
  id: number;
  code: string;
  name: string;
  /** 展示名：同股票多日重复买入时加序号（青岛啤酒2 / 青岛啤酒3） */
  displayName?: string;
  buyPrice: number;
  quantity: number;
  buyDate: string;
  buyTime: string;
  status: 'held' | 'sold';
  sellPrice?: number;
  sellDate?: string;
  sellTime?: string;
  sellReason?: string;
  profit?: number;
  profitPct?: number;
  currentPrice?: number;
  currentProfit?: number;
  currentProfitPct?: number;
  holdingDays?: number;
  intradayHigh?: number;
  intradayLow?: number;
  /** 补仓笔（2026-09-04）：持仓跌≥5% 后再次买入的那一笔 */
  isAvgLot?: boolean;
}

/** 持仓增强数据 */
export interface EnrichedHolding extends Holding {
  candidateDate?: string;
  candidatePrice?: number;
  low20?: number;
  /** 近 N 个交易日最低价（N 由 loss_30d_low_stop_days 条件决定，默认 20） */
  lowNDays?: number;
  sellHigh?: number;
  sellLow?: number;
  maxHighSince?: number;
  minLowSince?: number;
  intradayHigh?: number;
  intradayLow?: number;
  todayClose?: number;
  todayCloseProfit?: number;
  todayCloseProfitPct?: number;
  todayChangePct?: number;
}

/** 已卖股票明细 */
export interface StockSellDetail {
  id: number;
  code: string;
  name: string;
  /** 展示名：同股票多日重复买入时加序号（青岛啤酒2 / 青岛啤酒3） */
  displayName?: string;
  buyDate: string;
  buyPrice: number;
  quantity: number;
  sellDate?: string;
  sellPrice?: number;
  profit?: number;
  profitPct?: number;
  sellReason?: string;
  sellType: 'up' | 'down' | 'manual';
  candidateDate?: string;
  candidatePrice?: number;
  matchedRules: string[];
  todayPrice?: number;
  todayChangePct?: number;
}

export interface SellSummary {
  upCount: number;
  upProfit: number;
  downCount: number;
  downProfit: number;
  manualCount: number;
  manualProfit: number;
}

export interface Trade {
  id: number;
  code: string;
  name: string;
  direction: 'buy' | 'sell';
  price: number;
  quantity: number;
  amount: number;
  /** 本笔交易费用（买入=佣金+过户费；卖出=佣金+过户费+印花税） */
  fee?: number;
  holdingId?: number;
  tradeTime: string;
  profit?: number;
  profitPct?: number;
  reason?: string;
}

export interface Candidate {
  id: number;
  date: string;
  code: string;
  name: string;
  price: number;
  matchedRules: string;
  createdAt: string;
  turnoverRate?: number;
  /** 20 日累计振幅（线上已与回测 byte-level 对账一致，2026-09-06） */
  amplitude?: number;
}

export interface Account {
  initialCapital: number;
  balance: number;
  marketValue?: number;
  totalAssets?: number;
  totalProfit?: number;
  totalProfitPct?: number;
}

export interface InvestmentSummary {
  totalProfit: number;
  totalProfitPct: number;
  winRate: number;
  tradeCount: number;
  avgHoldingDays: number;
  maxProfit: number;
  maxLoss: number;
  profitFactor: number;
  /** 区间内累计交易费用（佣金+过户费+印花税） */
  totalFee?: number;
  trades: Trade[];
  equityCurve?: { date: string; value: number }[];
  stockDetails?: StockSellDetail[];
  sellSummary?: SellSummary;
}

// ===== 自动交易 =====

export interface AutoTradeStatus {
  enabled: boolean;
  maxStocks: number;
  buyAmountPerStock: number;
  buyTime: string;
  lastRunDate: string | null;
  lastRunResult: { bought: number; sold: number; paused: boolean; reason: string } | null;
}

export interface AutoTradeResult {
  bought: number;
  sold: number;
  paused: boolean;
  reason: string;
  boughtTrades: Trade[];
  soldTrades: Trade[];
}

export interface DailyPnl {
  id: number;
  date: string;
  buyCount: number;
  sellCount: number;
  realizedProfit: number;
  unrealizedProfit: number;
  totalAssets: number;
  balance: number;
  heldCount: number;
  note: string;
  createdAt: string;
}

// ===== 自检 =====

/** 自检发现的问题 */
export interface SelfCheckIssue {
  check: string;
  severity: 'info' | 'warn' | 'error';
  message: string;
  fixed: boolean;
  fix?: string;
}

/** 一条自检日志 */
export interface SelfCheckLog {
  id: number;
  checkTime: string;
  status: 'ok' | 'fixed' | 'error' | string;
  issuesFound: number;
  issuesFixed: number;
  durationMs: number;
  issues: SelfCheckIssue[];
}

// ===== API 响应 =====

export interface ApiResponse<T = any> {
  code: number;
  data: T;
  message: string;
}

// ===== 历史数据 =====

export interface HistoryDateInfo {
  date: string;
  count: number;
}

export interface HistoryStockData {
  code: string;
  name: string;
  open: number;
  close: number;
  high: number;
  low: number;
  volume: number;
  amount: number;
  amplitude: number;
  changePct: number;
  turnoverRate: number;
  isLimitUp: boolean;
}

export interface HistoryPreviewResult {
  data: HistoryStockData[];
  total: number;
  page: number;
  size: number;
  date: string;
}

/** 规则类型中文标签映射 */
export const RULE_TYPE_LABELS: Record<string, string> = {
  turnover_rate_consecutive: '连续N日换手率',
  price_from_low: '距最低价涨幅',
  exclude_st: '排除ST股',
  limit_up_history: '涨停历史',
  listing_days: '上市天数',
  profit_threshold: '盈利止盈',
  loss_threshold: '亏损止损',
  market_breadth_pause: '涨跌家数暂停',
  market_index_pause: '指数涨跌暂停',
  held_filter: '已持仓过滤',
  sell_cooldown: '止盈卖出冷却',
  loss_double: '止损加仓倍数',
  consecutive_loss_double: '连续亏损翻倍',
  max_price: '单价上限',
  same_day_sell_block: '当日禁止卖出',
  avg_down_buy: '持仓下跌补仓',
  avg_down_sell: '补仓回升清仓',
  volume_ratio_consecutive: '连续N日量比',
  price_amplitude: 'N日振幅',
  daily_max_buys: '每日最多买入',
  buy_sort_by: '撞顶时优先选',
  screen_low_30d_max_pct: '收盘价≤近30日最低点+%',
  loss_30d_low_stop_days: '近30日最低价止损',
  screen_up_days: '近N日至少M天上涨',
  float_market_cap_max: '流通市值上限',
  open_jump_cancel: '开盘涨幅过大取消',
  max_portfolio_value: '单笔持仓金额上限',
};
